<template>
  <v-dialog
    :value="show"
    max-width="950"
    persistent
    scrollable
    @input="$emit('update:show', $event)"
  >
    <v-card>
      <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
        <v-icon color="indigo" class="mr-2">mdi-database-search</v-icon>
        Pick Item from Inventory
        <v-spacer></v-spacer>
        <v-btn icon small @click="$emit('update:show', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <v-row dense class="mb-2">
          <v-col cols="12" sm="5">
            <v-select
              v-model="source"
              :items="sourceOptions"
              label="Item Source"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-filter-variant"
              @change="onSourceChange"
            ></v-select>
          </v-col>
          <v-col v-if="source === 'other_branch'" cols="12" sm="4">
            <v-select
              v-model="selectedBranchId"
              :items="otherBranches"
              item-text="branch_name"
              item-value="id"
              label="Select Branch"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-store"
              @change="fetchItems"
            ></v-select>
          </v-col>
          <v-col>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search items..."
              dense
              outlined
              clearable
              autofocus
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :loading="loading"
          :search="search"
          dense
          class="item-picker-table"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          @click:row="selectItem"
        >
          <template v-slot:item.stock="{ item }">
            <v-chip
              x-small
              label
              :color="item.stock > 0 ? 'green' : 'red'"
              dark
            >
              {{ item.stock }}
            </v-chip>
          </template>
          <template v-slot:item.lot_unit_cost="{ item }">
            {{ item.lot_unit_cost ? formatCurrency(item.lot_unit_cost) : "—" }}
          </template>
          <template v-slot:item.lot_is_vat="{ item }">
            <v-chip
              v-if="item.lot_is_vat !== null"
              x-small
              label
              :color="item.lot_is_vat ? 'blue' : 'grey'"
              dark
            >
              {{ item.lot_is_vat ? "VAT" : "Non-VAT" }}
            </v-chip>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:item.lot_date="{ item }">
            {{ item.lot_date || "—" }}
          </template>
          <template v-slot:item.branch_name="{ item }">
            <span class="caption">{{ item.branch_name || "—" }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              x-small
              label
              :color="item.status === 'Active' ? 'green' : 'red lighten-1'"
              dark
            >
              {{ item.status }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      loading: false,
      source: "current_branch",
      selectedBranchId: null,
      pickerItems: [],
      sourceOptions: [
        { text: "Current Branch", value: "current_branch" },
        { text: "Stocks from Other Branch", value: "other_branch" },
        { text: "All Items (incl. deleted)", value: "all_items" },
      ],
      headers: [
        { text: "Item Name", value: "item_name", width: "200px" },
        { text: "Type", value: "item_type", width: "100px" },
        { text: "Branch", value: "branch_name", width: "100px" },
        { text: "Supplier", value: "lot_supplier", width: "130px" },
        { text: "Unit", value: "unit", width: "60px" },
        { text: "Stock", value: "stock", width: "70px", align: "center" },
        {
          text: "Unit Cost",
          value: "lot_unit_cost",
          width: "100px",
          align: "right",
        },
        { text: "Purchase Date", value: "lot_date", width: "110px" },
        { text: "VAT", value: "lot_is_vat", width: "80px", align: "center" },
        { text: "Status", value: "status", width: "80px", align: "center" },
      ],
    };
  },
  computed: {
    ...mapGetters("item", ["items", "all_items"]),
    ...mapGetters("branch", ["branches"]),
    ...mapGetters("auth", ["user"]),
    otherBranches() {
      if (!this.user) return this.branches;
      return this.branches.filter((b) => b.id !== this.user.branch_id);
    },
    filteredItems() {
      return this.pickerItems.map((item) => {
        const lot = item.latest_lot || null;
        const typeName = item.item_type ? item.item_type.item_type : "";
        const subTypeName = item.item_sub_type
          ? item.item_sub_type.item_sub_type
          : "";
        const typeDisplay =
          typeName && subTypeName
            ? typeName + " - " + subTypeName
            : typeName || "";
        const branchName = item.branch ? item.branch.branch_name : "";
        const isDeleted = item.is_deleted === 1 || item.deleted_at;
        return {
          id: item.id,
          item_name: item.item_name,
          item_type: typeDisplay,
          branch_name: branchName,
          unit: this.normalizeUnit(item.unit),
          stock: item.quantity != null ? item.quantity : 0,
          supplier: item.supplier || "",
          supplier_price: parseFloat(item.supplier_price) || 0,
          is_vat: item.is_vat || false,
          lot_supplier: lot ? lot.supplier : item.supplier || "",
          lot_unit_cost: lot ? parseFloat(lot.unit_cost) : null,
          lot_is_vat: lot ? lot.is_vat : null,
          lot_shipping_cost: lot ? parseFloat(lot.shipping_cost) || 0 : 0,
          lot_date:
            lot && lot.purchase_date
              ? lot.purchase_date
              : lot && lot.created_at
              ? lot.created_at.substring(0, 10)
              : "",
          status: isDeleted ? "Deleted" : "Active",
          _raw: item,
        };
      });
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.search = "";
        this.source = "current_branch";
        this.selectedBranchId = null;
        this.fetchItems();
        if (this.branches.length === 0) {
          this.get_branches();
        }
      }
    },
  },
  methods: {
    ...mapActions("item", ["get_items", "get_all_items"]),
    ...mapActions("branch", ["get_branches"]),
    normalizeUnit(raw) {
      if (!raw) return "pc";
      const val = raw.trim().toLowerCase();
      const map = {
        pcs: "pc",
        pc: "pc",
        piece: "pc",
        pieces: "pc",
        set: "set",
        sets: "set",
        unit: "unit",
        units: "unit",
        lot: "lot",
        lots: "lot",
        pack: "pack",
        packs: "pack",
        roll: "roll",
        rolls: "roll",
        meter: "meter",
        meters: "meter",
        m: "meter",
        box: "box",
        boxes: "box",
        dozen: "pack",
      };
      return map[val] || "pc";
    },
    onSourceChange() {
      this.selectedBranchId = null;
      this.pickerItems = [];
      if (this.source !== "other_branch") {
        this.fetchItems();
      }
    },
    async fetchItems() {
      this.loading = true;
      try {
        if (this.source === "all_items") {
          await this.get_all_items();
          this.pickerItems = this.all_items;
        } else if (this.source === "other_branch" && this.selectedBranchId) {
          const response = await this.$store.dispatch(
            "item/get_items_from_branch",
            this.selectedBranchId
          );
          this.pickerItems = response;
        } else {
          await this.get_items();
          this.pickerItems = this.items;
        }
      } catch (e) {
        console.error("Failed to fetch items:", e);
      } finally {
        this.loading = false;
      }
    },
    selectItem(row) {
      const lot = row._raw.latest_lot || null;
      const payload = {
        item_name: row.item_name,
        item_type: row.item_type || "Hardware",
        unit: this.normalizeUnit(row.unit),
        supplier: lot ? lot.supplier || row.supplier : row.supplier,
        supplier_price: lot
          ? parseFloat(lot.unit_cost) || 0
          : parseFloat(row.supplier_price) || 0,
        is_vat: lot ? !!lot.is_vat : !!row.is_vat,
        freight_in_per_unit: lot ? parseFloat(lot.shipping_cost) || 0 : 0,
      };
      this.$emit("select", payload);
      this.$emit("update:show", false);
    },
    formatCurrency(val) {
      const num = parseFloat(val) || 0;
      return (
        "₱ " +
        num.toLocaleString("en-PH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
  },
};
</script>

<style scoped>
.item-picker-table >>> tbody tr {
  cursor: pointer;
}
.item-picker-table >>> tbody tr:hover {
  background-color: #e8eaf6 !important;
}
</style>
